import { Table } from '@tanstack/react-table'
import { useMemo } from 'react'

export function useFrozenColumnsV2<TData>(
  table: Table<TData>,
  freezePane: number | undefined,
  computedColWidths: Record<string, number>
) {
  // Use getVisibleLeafColumns() to exclude hidden columns from frozen pane calculations
  const leafColumns = table.getVisibleLeafColumns()

  const frozenColOffsetsById = useMemo(() => {
    if (!freezePane) return {}
    const offsets: Record<string, number> = {}
    let acc = 0
    for (let i = 0; i < leafColumns.length && i < freezePane; i++) {
      const col = leafColumns[i]
      offsets[col.id] = acc
      // With border-box sizing (Tailwind default), an explicit `size` value set as
      // CSS `width` already includes padding and border, so no extra padding offset
      // is needed. For columns where the explicit size is smaller than the content
      // needs (e.g. the expander column with size: 24 but 32px padding), the browser
      // expands the column — in that case the measured width (+1 for border-r) is
      // more accurate. Using Math.max handles both scenarios correctly.
      const explicit = col.columnDef.size ?? 0
      const measured = computedColWidths[col.id]
      acc += Math.max(explicit, measured != null ? measured + 1 : 0)
    }
    return offsets
  }, [freezePane, leafColumns, computedColWidths])

  return { leafColumns, frozenColOffsetsById }
}
